export const APPOINTMENT_STATUSES = ["SCHEDULED", "COMPLETED", "CANCELLED", "RESCHEDULED"];

export const APPOINTMENT_VISIBILITY = {
  ORG: "ORG",
  PRIVATE: "PRIVATE",
};

export const visibilityLabel = (visibility) => {
  if (visibility === APPOINTMENT_VISIBILITY.PRIVATE) return "Private";
  return "Shared";
};

export const isPrivateAppointment = (appt) => appt?.visibility === APPOINTMENT_VISIBILITY.PRIVATE;

export const statusColor = {
  SCHEDULED: "primary",
  COMPLETED: "success",
  CANCELLED: "error",
  RESCHEDULED: "warning",
};

export const statusBgColor = {
  SCHEDULED: "#dbeafe",
  COMPLETED: "#dcfce7",
  CANCELLED: "#fee2e2",
  RESCHEDULED: "#fef3c7",
};

export const statusTextColor = {
  SCHEDULED: "#1d4ed8",
  COMPLETED: "#15803d",
  CANCELLED: "#b91c1c",
  RESCHEDULED: "#b45309",
};

export const participantCount = (appt) => {
  const list = Array.isArray(appt?.participants) ? appt.participants : [];
  return list.length || (appt?.clientName || appt?.clientId ? 1 : 0);
};

/** All client ids linked to an appointment (primary + participants). */
export const appointmentClientIds = (appt) => {
  const ids = new Set();
  if (appt?.clientId != null && appt.clientId !== "") ids.add(Number(appt.clientId));
  (Array.isArray(appt?.participants) ? appt.participants : []).forEach((p) => {
    if (p?.clientId != null && p.clientId !== "") ids.add(Number(p.clientId));
  });
  return [...ids].filter((id) => Number.isFinite(id));
};

/** Hard tenancy: appointment is visible only if it involves one of the allowed clients. */
export const appointmentMatchesClients = (appt, allowedClientIds) => {
  if (!Array.isArray(allowedClientIds) || allowedClientIds.length === 0) return false;
  const allowed = new Set(allowedClientIds.map(Number).filter((id) => Number.isFinite(id)));
  if (!allowed.size) return false;
  return appointmentClientIds(appt).some((id) => allowed.has(id));
};

export const clientDisplayLabel = (appt) => {
  const primary = appt?.clientName || "—";
  const extra = participantCount(appt) - 1;
  return extra > 0 ? `${primary} +${extra}` : primary;
};

export const participantNames = (appt) => {
  const list = Array.isArray(appt?.participants) ? appt.participants : [];
  if (list.length) return list.map((p) => p.clientName).filter(Boolean);
  return [appt?.clientName].filter(Boolean);
};

export const parseApptDate = (appt) => {
  if (!appt?.appointmentDate) return null;
  const dateStr = String(appt.appointmentDate).split("T")[0];
  const [y, m, d] = dateStr.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

/** Format start – end for display; legacy rows without end time show start only. */
export const formatAppointmentTimeDisplay = (appt) => {
  const start = (appt?.appointmentTime || "").trim();
  const end = (appt?.appointmentEndTime || "").trim();
  if (!start) return "";
  if (!end) return start;
  return `${start} – ${end}`;
};

const parseTimeToMinutes = (time) => {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) return null;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

/** Human-readable duration when both start and end are set (e.g. "1h 30m"). */
export const formatAppointmentDuration = (appt) => {
  const startMins = parseTimeToMinutes(appt?.appointmentTime);
  const endMins = parseTimeToMinutes(appt?.appointmentEndTime);
  if (startMins == null || endMins == null || endMins <= startMins) return "";
  const diff = endMins - startMins;
  const hours = Math.floor(diff / 60);
  const mins = diff % 60;
  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours) return `${hours}h`;
  return `${mins}m`;
};

/** Client-side validation matching backend rules. Returns error message or null. */
export const validateAppointmentEndTime = (startTime, endTime) => {
  const end = (endTime || "").trim();
  if (!end) return null;
  const start = (startTime || "").trim();
  if (!start) return "End time requires a start time.";
  const startMins = parseTimeToMinutes(start);
  const endMins = parseTimeToMinutes(end);
  if (startMins == null || endMins == null) return "Times must be in HH:mm format.";
  if (endMins <= startMins) return "End time must be after start time.";
  return null;
};

export const dateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const groupAppointmentsByDate = (appointments = []) => {
  const map = {};
  appointments.forEach((appt) => {
    const parsed = parseApptDate(appt);
    if (!parsed) return;
    const key = dateKey(parsed);
    if (!map[key]) map[key] = [];
    map[key].push(appt);
  });
  Object.values(map).forEach((list) =>
    list.sort((a, b) => (a.appointmentTime || "").localeCompare(b.appointmentTime || ""))
  );
  return map;
};
